import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  useMediaQuery,
  CircularProgress,
  Select,
  FormControl,
  InputLabel,
  Container,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { createEvent, CreateEventData } from '../services/eventService';
import { s3Service } from '../services/s3Service';
import ComingSoonTag from '../components/ComingSoonTag';
import AppMenu from '../components/AppMenu';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { DEFAULT_TIMEZONE } from '../config';

const sections = ['Event Details', 'Social Settings', 'Emailers', 'Surveys'];

const CreateEvent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { showSnackbar } = useSnackbar();

  const [selectedSection, setSelectedSection] = useState(0);
  
  // Add state to track touched fields and form submission attempts
  const [touchedFields, setTouchedFields] = useState<{ [key: string]: boolean }>({});
  const [formSubmitAttempted, setFormSubmitAttempted] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    venue: '',
    eirCode: '',
    imageUrl: '',
    isDraft: true,
    isVirtual: false,
    isFreeOfCharge: true,
    sellingPrice: '0',
    anchorPrice: '0',
    capacity: '',
    artistDescription: '',
    refundPolicy: '',
  });

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formProgress, setFormProgress] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) navigate('/');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Update progress whenever form data changes
    setFormProgress(calculateFormProgress());
  }, [formData]);

  // Helper function to count words
  const countWords = (text: string): number => {
    if (!text || text.trim() === '') return 0;
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  // Calculate form completion percentage based on filled fields
  const calculateFormProgress = () => {
    const requiredFields = [
      'title',
      'description',
      'startTime',
      'endTime',
      'venue',
      'eirCode',
      'refundPolicy',
      'isDraft',
      'isVirtual',
      'isFreeOfCharge',
      'capacity'
    ];
    
    const optionalFields = [
      'imageUrl',
    ];
    
    // Count filled required fields
    const filledRequiredFields = requiredFields.filter(field => 
      formData[field as keyof typeof formData] && String(formData[field as keyof typeof formData]).trim() !== ''
    ).length;
    
    // Count filled optional fields (these count less toward total progress)
    const filledOptionalFields = optionalFields.filter(field => 
      formData[field as keyof typeof formData] && String(formData[field as keyof typeof formData]).trim() !== ''
    ).length;
    
    // Calculate weighted progress (required fields count more)
    const requiredWeight = 0.8;
    const optionalWeight = 0.2;
    
    const requiredProgress = (filledRequiredFields / requiredFields.length) * requiredWeight;
    const optionalProgress = (filledOptionalFields / optionalFields.length) * optionalWeight;
    
    // Calculate total progress (0 to 1)
    const totalProgress = requiredProgress + optionalProgress;
    
    // Return percentage rounded to nearest integer
    return Math.round(totalProgress * 100);
  };

  // Check which sections are incomplete
  const getIncompleteSections = () => {
    const sectionsStatus = {
      0: false, // Event Details
      1: true,  // Social Settings (locked)
      2: true,  // Emailers (locked)
      3: true,  // Surveys (locked)
    };
    
    // Check Event Details section
    const eventDetailsFields = ['title', 'description', 'startTime', 'endTime', 'venue'];
    sectionsStatus[0] = eventDetailsFields.every(field => 
      formData[field as keyof typeof formData] && String(formData[field as keyof typeof formData]).trim() !== ''
    );
    
    return Object.entries(sectionsStatus)
      .filter(([_, isComplete]) => !isComplete)
      .map(([sectionIndex, _]) => parseInt(sectionIndex));
  };

  // Navigate to the first incomplete section
  const navigateToIncompleteSection = () => {
    const incompleteSections = getIncompleteSections();
    if (incompleteSections.length > 0) {
      setSelectedSection(incompleteSections[0]);
    }
  };

  // Helper function to mark a field as touched
  const handleBlur = (field: string) => {
    setTouchedFields(prev => ({
      ...prev,
      [field]: true
    }));
  };
  
  // Helper function to check if error should be shown for a field
  const shouldShowError = (fieldName: string, isRequired: boolean, hasValue: boolean) => {
    // Show error if:
    // 1. Field is required AND
    // 2. Field is empty AND
    // 3. Either the form submission was attempted OR the field was touched (focused and then blurred)
    return isRequired && !hasValue && (formSubmitAttempted || touchedFields[fieldName]);
  };

  const handleImageUpload = async (file: File) => {
    // Mark the imageUrl field as touched
    setTouchedFields(prev => ({
      ...prev,
      'imageUrl': true
    }));
    
    const validTypes = s3Service.getConfig().allowedFileTypes;
    const maxSize = s3Service.getConfig().maxFileSize;

    if (!validTypes.includes(file.type) || file.size > maxSize) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const url = await s3Service.uploadImage(file, 'events', (progress) => setUploadProgress(progress));
      setFormData((prev) => ({ ...prev, imageUrl: url }));
    } catch (err) {
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  // Helper function to format time for display (for datetime-local input)
  const formatTimeForDisplay = (datetimeLocal: string) => {
    if (!datetimeLocal) return '';
    // Extract time part from datetime-local format (YYYY-MM-DDTHH:MM)
    const timePart = datetimeLocal.split('T')[1];
    return timePart || '';
  };

  // Helper function to format date for display (for datetime-local input)
  const formatDateForDisplay = (datetimeLocal: string) => {
    if (!datetimeLocal) return '';
    // Extract date part from datetime-local format (YYYY-MM-DDTHH:MM)
    const datePart = datetimeLocal.split('T')[0];
    return datePart || '';
  };

  // Helper function to convert datetime-local to ISO string with Ireland timezone
  const convertToIrelandISO = (datetimeLocal: string) => {
    if (!datetimeLocal) return '';
    
    // Parse the datetime-local string (format: YYYY-MM-DDTHH:MM)
    const [datePart, timePart] = datetimeLocal.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    
    // Create a temporary date to determine Ireland's timezone offset at this specific date
    const tempDate = new Date(year, month - 1, day, hours, minutes);
    
    // Get the timezone offset for Ireland at this specific date
    const irelandTime = new Date(tempDate.toLocaleString("en-US", {timeZone: DEFAULT_TIMEZONE}));
    const utcTime = new Date(tempDate.toLocaleString("en-US", {timeZone: "UTC"}));
    const offsetMs = irelandTime.getTime() - utcTime.getTime();
    const offsetHours = Math.floor(offsetMs / (1000 * 60 * 60));
    
    // Format offset string
    const offsetSign = offsetHours >= 0 ? '+' : '-';
    const offsetString = `${offsetSign}${Math.abs(offsetHours).toString().padStart(2, '0')}:00`;
    
    // Format the date as ISO string with Ireland timezone offset
    // This represents the datetime as if it's in Ireland timezone
    const formattedYear = year.toString();
    const formattedMonth = month.toString().padStart(2, '0');
    const formattedDay = day.toString().padStart(2, '0');
    const formattedHours = hours.toString().padStart(2, '0');
    const formattedMinutes = minutes.toString().padStart(2, '0');
    
    return `${formattedYear}-${formattedMonth}-${formattedDay}T${formattedHours}:${formattedMinutes}:00${offsetString}`;
  };

  const handleSubmit = async () => {
    // Mark form as having a submission attempt
    setFormSubmitAttempted(true);
    
    // Check if form has all required fields before proceeding
    const requiredFields = [
      'title',
      'description',
      'startTime',
      'endTime',
      'venue',
      'eirCode',
      'refundPolicy',
      'isDraft',
      'isVirtual',
      'isFreeOfCharge',
      'capacity'
    ];
    
    // Validate refundPolicy word count
    const refundPolicyWordCount = countWords(formData.refundPolicy);
    if (!formData.refundPolicy || formData.refundPolicy.trim() === '') {
      navigateToIncompleteSection();
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }
    if (refundPolicyWordCount > 200) {
      showSnackbar('Refund policy must be 200 words or less', 'error');
      return;
    }
    
    const missingRequiredFields = requiredFields.filter(field => {
      const value = formData[field as keyof typeof formData];
      if (typeof value === 'boolean') return false; // Boolean fields are always valid
      
      if (field === 'capacity') {
        return !value || isNaN(Number(value)) || Number(value) <= 0;
      }
      if (field === 'refundPolicy') {
        return false; // Already validated above
      }
      return !value || String(value).trim() === '';
    });
    
    if (missingRequiredFields.length > 0) {
      navigateToIncompleteSection();
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }
    
    setLoading(true);
    try {
      // Convert form data to match API requirements
      const eventData: CreateEventData = {
        title: formData.title,
        description: formData.description,
        venue: formData.venue,
        eirCode: formData.eirCode,
        startTime: convertToIrelandISO(formData.startTime),
        endTime: convertToIrelandISO(formData.endTime),
        imageUrl: formData.imageUrl,
        isDraft: String(Boolean(formData.isDraft)),
        isVirtual: String(Boolean(formData.isVirtual)),
        isFreeOfCharge: String(Boolean(formData.isFreeOfCharge)),
        sellingPrice: formData.isFreeOfCharge ? '0' : String(Number(formData.sellingPrice)),
        anchorPrice: formData.isFreeOfCharge ? '0' : String(Number(formData.anchorPrice)),
        capacity: String(Number(formData.capacity)),
        artistDescription: formData.artistDescription || undefined,
        refundPolicy: formData.refundPolicy,
      };
      
      const response = await createEvent(eventData);
      showSnackbar('Event created successfully!', 'success');
      // Redirect to the specific event page
      if (response && response.id) {
        navigate(`/events/${response.id}`);
      } else {
        // Fallback to events listing if no ID is returned
        navigate('/events');
      }
    } catch (err) {
      console.error('Event creation failed:', err);
      showSnackbar('Failed to create event', 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderSectionContent = () => {
    // Define required fields
    const requiredFields = [
      'title',
      'description',
      'startTime',
      'endTime',
      'venue',
      'refundPolicy',
      'isDraft',
      'isVirtual',
      'isFreeOfCharge',
      'capacity'
    ];
    
    // Helper function to check if a field is required
    const isFieldRequired = (fieldName: string) => {
      return requiredFields.includes(fieldName);
    };
    
    switch (selectedSection) {
      case 0:
        return (
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Event Details */}
            <Typography variant="h6" color="primary" sx={{ mb: 1 }}>Basic Information</Typography>
            <TextField 
              label="Title" 
              fullWidth 
              value={formData.title} 
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required={isFieldRequired('title')}
              helperText={shouldShowError('title', isFieldRequired('title'), !!formData.title) ? "Field required for form completion" : ""}
              error={shouldShowError('title', isFieldRequired('title'), !!formData.title)}
              onBlur={() => handleBlur('title')}
            />
            <TextField 
              label="Description" 
              fullWidth 
              multiline 
              rows={4} 
              value={formData.description} 
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required={isFieldRequired('description')}
              helperText={shouldShowError('description', isFieldRequired('description'), !!formData.description) ? "Field required for form completion" : ""}
              error={shouldShowError('description', isFieldRequired('description'), !!formData.description)}
              onBlur={() => handleBlur('description')}
            />
            <TextField 
              label="Artist/Performer Description" 
              fullWidth 
              multiline 
              rows={4} 
              value={formData.artistDescription} 
              onChange={(e) => setFormData({ ...formData, artistDescription: e.target.value })}
              placeholder="Tell us about the artist, their background, achievements, or what to expect from their performance"
              helperText="Optional: A brief biography or description of the artist"
            />
            <TextField 
              label="Start Date & Time" 
              type="datetime-local" 
              fullWidth 
              InputLabelProps={{ shrink: true }} 
              value={formData.startTime ? formatDateForDisplay(formData.startTime) + 'T' + formatTimeForDisplay(formData.startTime) : ''}
              onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
              required={isFieldRequired('startTime')}
              helperText={shouldShowError('startTime', isFieldRequired('startTime'), !!formData.startTime) ? "Field required for form completion" : ""}
              error={shouldShowError('startTime', isFieldRequired('startTime'), !!formData.startTime)}
              onBlur={() => handleBlur('startTime')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  cursor: 'pointer',
                  '& input': {
                    cursor: 'pointer'
                  }
                }
              }}
            />
            <TextField 
              label="End Date & Time" 
              type="datetime-local" 
              fullWidth 
              InputLabelProps={{ shrink: true }} 
              value={formData.endTime ? formatDateForDisplay(formData.endTime) + 'T' + formatTimeForDisplay(formData.endTime) : ''}
              onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
              required={isFieldRequired('endTime')}
              helperText={shouldShowError('endTime', isFieldRequired('endTime'), !!formData.endTime) ? "Field required for form completion" : ""}
              error={shouldShowError('endTime', isFieldRequired('endTime'), !!formData.endTime)}
              onBlur={() => handleBlur('endTime')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  cursor: 'pointer',
                  '& input': {
                    cursor: 'pointer'
                  }
                }
              }}
            />
            <TextField 
              label="Venue" 
              fullWidth 
              value={formData.venue} 
              onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
              required={isFieldRequired('venue')}
              helperText={shouldShowError('venue', isFieldRequired('venue'), !!formData.venue) ? "Field required for form completion" : ""}
              error={shouldShowError('venue', isFieldRequired('venue'), !!formData.venue)}
              onBlur={() => handleBlur('venue')}
            />
            <TextField 
              label="EIR Code" 
              fullWidth 
              value={formData.eirCode} 
              onChange={(e) => setFormData({ ...formData, eirCode: e.target.value })}
              required={isFieldRequired('eirCode')}
              helperText={shouldShowError('eirCode', isFieldRequired('eirCode'), !!formData.eirCode) ? "Field required for form completion" : "Irish postal code (e.g., D02 RD28)"}
              error={shouldShowError('eirCode', isFieldRequired('eirCode'), !!formData.eirCode)}
              onBlur={() => handleBlur('eirCode')}
              placeholder="e.g., D02 RD28"
            />
            <TextField 
              label="Refund Policy" 
              fullWidth 
              multiline
              rows={6}
              value={formData.refundPolicy} 
              onChange={(e) => setFormData({ ...formData, refundPolicy: e.target.value })}
              required={isFieldRequired('refundPolicy')}
              helperText={
                shouldShowError('refundPolicy', isFieldRequired('refundPolicy'), !!formData.refundPolicy) 
                  ? "Field required for form completion" 
                  : countWords(formData.refundPolicy) > 200
                    ? `Word count: ${countWords(formData.refundPolicy)}/200 (exceeds limit)`
                    : `Word count: ${countWords(formData.refundPolicy)}/200`
              }
              error={shouldShowError('refundPolicy', isFieldRequired('refundPolicy'), !!formData.refundPolicy) || countWords(formData.refundPolicy) > 200}
              onBlur={() => handleBlur('refundPolicy')}
              placeholder="Describe your refund policy (maximum 200 words)"
            />

            {/* Event Settings */}
            <Typography variant="h6" color="primary" sx={{ mt: 3, mb: 1 }}>Event Settings</Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={formData.isVirtual ? 'virtual' : 'in-person'}
                  onChange={(e) => setFormData({ ...formData, isVirtual: e.target.value === 'virtual' })}
                  label="Event Type"
                >
                  <MenuItem value="in-person">In-person</MenuItem>
                  <MenuItem value="virtual">Virtual</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Price Type</InputLabel>
                <Select
                  value={formData.isFreeOfCharge ? 'free' : 'paid'}
                  onChange={(e) => {
                    const isFree = e.target.value === 'free';
                    setFormData({ 
                      ...formData, 
                      isFreeOfCharge: isFree,
                      sellingPrice: isFree ? '0' : formData.sellingPrice,
                      anchorPrice: isFree ? '0' : formData.anchorPrice
                    });
                  }}
                  label="Price Type"
                >
                  <MenuItem value="free">Free</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Price fields - shown only for paid events */}
            {!formData.isFreeOfCharge && (
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  label="Selling Price (€)"
                  type="number"
                  fullWidth
                  value={formData.sellingPrice}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || (!isNaN(Number(value)) && Number(value) >= 0)) {
                      setFormData({ ...formData, sellingPrice: value });
                    }
                  }}
                  InputProps={{
                    inputProps: { min: 0, step: 0.01 }
                  }}
                  helperText="The current price of the ticket"
                />
                <TextField
                  label="Original Price (€)"
                  type="number"
                  fullWidth
                  value={formData.anchorPrice}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || (!isNaN(Number(value)) && Number(value) >= 0)) {
                      setFormData({ ...formData, anchorPrice: value });
                    }
                  }}
                  InputProps={{
                    inputProps: { min: 0, step: 0.01 }
                  }}
                  helperText="Original price (for showing discounts)"
                />
              </Box>
            )}

            <TextField 
              label="Capacity" 
              type="number"
              fullWidth 
              value={formData.capacity}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || (!isNaN(Number(value)) && Number(value) > 0)) {
                  setFormData({ ...formData, capacity: value });
                }
              }}
              required={isFieldRequired('capacity')}
              helperText={shouldShowError('capacity', isFieldRequired('capacity'), !!formData.capacity) ? "Field required for form completion" : ""}
              error={shouldShowError('capacity', isFieldRequired('capacity'), !!formData.capacity)}
              onBlur={() => handleBlur('capacity')}
              InputProps={{
                inputProps: { min: 1 }
              }}
            />

            {/* Images */}
            <Typography variant="h6" color="primary" sx={{ mt: 3, mb: 1 }}>Event Image</Typography>
            
            {/* Recommended size info */}
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium' }}>
                📏 Recommended Image Size:
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                • Dimensions: 1920 × 1080 pixels (16:9 aspect ratio)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                • Maximum file size: 5MB
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                • Supported formats: JPEG, PNG, GIF, WebP
              </Typography>
            </Box>
            
            <Button variant="outlined" component="label">
              Upload Image
              <input hidden accept="image/*" type="file" onChange={(e) => e.target.files && handleImageUpload(e.target.files[0])} />
            </Button>
            {isUploading && <CircularProgress variant="determinate" value={uploadProgress} />}
            {formData.imageUrl ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Image Preview (as it will appear on event cards):
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 300,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <Box
                    component="img"
                    src={formData.imageUrl}
                    alt="Event preview"
                    sx={{
                      width: '100%',
                      height: 180,
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  This is how your image will appear on event cards
                </Typography>
              </Box>
            ) : (
              <Typography 
                color="text.secondary" 
                variant="caption" 
                sx={{ mt: 1 }}
              >
                Optional: Upload an image for your event
              </Typography>
            )}
          </Box>
        );
      case 1:
        return (
          <Box 
            display="flex" 
            flexDirection="column" 
            gap={2}
            sx={{
              opacity: 0.6,
              pointerEvents: 'none',
              position: 'relative'
            }}
          >
            <ComingSoonTag 
              variant="box" 
              size="large"
              position="absolute"
              absolutePosition={{
                top: '50%',
                left: '50%'
              }}
              sx={{ 
                zIndex: 1,
                transform: 'translate(-50%, -50%)'
              }}
            />
            <TextField 
              label="Facebook" 
              fullWidth 
              disabled
            />
            <TextField 
              label="Instagram" 
              fullWidth 
              disabled
            />
            <TextField 
              label="Twitter" 
              fullWidth 
              disabled
            />
            <TextField 
              label="TikTok" 
              fullWidth 
              disabled
            />
          </Box>
        );
      case 2:
        return (
          <Box 
            display="flex" 
            flexDirection="column" 
            gap={2}
            sx={{
              opacity: 0.6,
              pointerEvents: 'none',
              position: 'relative'
            }}
          >
            <ComingSoonTag 
              variant="box" 
              size="large"
              position="absolute"
              absolutePosition={{
                top: '50%',
                left: '50%'
              }}
              sx={{ 
                zIndex: 1,
                transform: 'translate(-50%, -50%)'
              }}
            />
            <TextField 
              label="Welcome Email Template" 
              fullWidth 
              multiline 
              rows={4}
              disabled
            />
            <TextField 
              label="Reminder Email Template" 
              fullWidth 
              multiline 
              rows={4}
              disabled
            />
            <TextField 
              label="Thank You Email Template" 
              fullWidth 
              multiline 
              rows={4}
              disabled
            />
          </Box>
        );
      case 3:
        return (
          <Box 
            display="flex" 
            flexDirection="column" 
            gap={2}
            sx={{
              opacity: 0.6,
              pointerEvents: 'none',
              position: 'relative'
            }}
          >
            <ComingSoonTag 
              variant="box" 
              size="large"
              position="absolute"
              absolutePosition={{
                top: '50%',
                left: '50%'
              }}
              sx={{ 
                zIndex: 1,
                transform: 'translate(-50%, -50%)'
              }}
            />
            <TextField 
              label="Pre-Event Survey" 
              fullWidth 
              multiline 
              rows={4}
              disabled
            />
            <TextField 
              label="Post-Event Survey" 
              fullWidth 
              multiline 
              rows={4}
              disabled
            />
            <TextField 
              label="Feedback Form" 
              fullWidth 
              multiline 
              rows={4}
              disabled
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppMenu />
      
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
        {/* Page Title with Progress Indicator */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
            Create Event
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Add your event details and settings
          </Typography>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Form Progress
            </Typography>
            <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
              {formProgress}%
            </Typography>
          </Box>
          <Box
            sx={{
              width: '100%',
              height: 8,
              bgcolor: 'divider',
              borderRadius: 4,
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                width: `${formProgress}%`,
                height: '100%',
                bgcolor: 'primary.main',
                borderRadius: 4,
                transition: 'width 0.3s ease'
              }}
            />
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={selectedSection} 
            onChange={(_, newValue) => setSelectedSection(newValue)}
            variant={isMobile ? "scrollable" : "fullWidth"}
            scrollButtons={isMobile ? "auto" : false}
          >
            {sections.map((section, index) => (
              <Tab 
                key={section} 
                label={section} 
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 48
                }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Section Content */}
        <Box sx={{ mb: 4 }}>
          {renderSectionContent()}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/events')}
            sx={{ 
              px: 4, 
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || isUploading}
            sx={{ 
              px: 4, 
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              bgcolor: '#EC4899',
              '&:hover': {
                bgcolor: '#DB2777'
              }
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Create Event'}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default CreateEvent;
